import {
  IconAlertTriangle,
  IconCloudOff,
  IconRefresh,
  IconWifiOff,
} from "@tabler/icons-react";
import { ErrorInfo } from "react";
import { __ } from "@common/helpers/i18n";
import useReportError from "@common/hooks/useReportError";
import { getErrorText, isStaleChunkError } from "@app/helpers/staleChunkRecovery";
import ErrorPageShell from "./ErrorPageShell";

const getUrl = () => window.location.toString();

function getTraceIdFromUrl() {
  const params = new URLSearchParams(window.location.search);

  return (
    params.get("trace_id") ||
    params.get("traceId") ||
    params.get("trace-id") ||
    params.get("request_id") ||
    params.get("requestId") ||
    undefined
  );
}

function getRuntimeErrorState(error: unknown) {
  const isOffline =
    typeof navigator !== "undefined" &&
    "onLine" in navigator &&
    !navigator.onLine;

  if (isOffline) {
    return {
      code: "",
      description: __(
        "Your device is offline. Check your connection and try again.",
      ),
      icon: IconWifiOff,
      showCode: false,
      title: __("No internet connection"),
      tone: "warning" as const,
    };
  }

  if (isStaleChunkError(error)) {
    return {
      code: "",
      description: __(
        "The page could not be loaded. Refresh the page or try again in a moment.",
      ),
      icon: IconRefresh,
      showCode: false,
      title: __("A new version is ready"),
      tone: "warning" as const,
    };
  }

  const errorText = getErrorText(error);
  const isNetworkError =
    /Failed to fetch|NetworkError|Load failed|The Internet connection appears to be offline/i.test(
      errorText,
    );

  if (isNetworkError) {
    return {
      code: "",
      description: __(
        "The request did not reach the server or the browser blocked the response. Check your connection and try again.",
      ),
      icon: IconCloudOff,
      showCode: false,
      title: __("Could not connect to the server"),
      tone: "warning" as const,
    };
  }

  return {
    code: "500",
    description: __(
      "The page could not be loaded. Refresh the page or try again in a moment.",
    ),
    icon: IconAlertTriangle,
    showCode: true,
    title: __("Something went wrong."),
    tone: "danger" as const,
  };
}

export default function Error500({
  error,
  info,
  traceId: traceIdProp,
}: {
  error?: unknown;
  info?: ErrorInfo;
  traceId?: string;
}) {
  const reportedTraceId = useReportError(error, info, getUrl);
  const traceId = traceIdProp || getTraceIdFromUrl() || reportedTraceId;
  const errorState = getRuntimeErrorState(error);

  return (
    <ErrorPageShell
      code={errorState.code}
      icon={errorState.icon}
      title={errorState.title}
      description={errorState.description}
      showCode={errorState.showCode}
      tone={errorState.tone}
      traceId={traceId || undefined}
    />
  );
}
